import { useMemo } from "react"

import { Floor } from "shared/types/Floor"
import { ProductPosition } from "shared/types/ProductPosition"
import {
  ProjectChild,
  isProjectFloor,
  isProjectProductPosition,
  isProjectRoom
} from "shared/types/Project"
import { Room } from "shared/types/Room"

// Tab identifiers for the export overlay. These mirror the values used by the
// project page's tab bar, but are declared locally so the widget does not
// import upward from the pages layer.
export const UNASSIGNED_ROOMS_TAB_ID = "unassigned-rooms"
export const UNASSIGNED_PRODUCTS_TAB_ID = "unassigned-products"

export type MusterheftExportChild = Floor | Room | ProductPosition

interface UseMusterheftExportTabStateArgs {
  projectChildren: ProjectChild[]
  activeTabID: string | null
}

interface UseMusterheftExportTabStateReturn {
  floors: Floor[]
  unassignedRoomsCount: number
  unassignedProductsCount: number
  resolvedTabID: string | null
  visibleChildren: MusterheftExportChild[]
}

/**
 * Derives the export overlay's floor tabs, unassigned-element counts, the
 * resolved active tab, and the children visible under it from a project's
 * children tree.
 *
 * Unlike the project page's tab state, this is pure over `projectChildren` and
 * a locally-held `activeTabID` — the overlay keeps its own tab state and never
 * reads or writes the URL.
 */
export function useMusterheftExportTabState({
  projectChildren,
  activeTabID
}: UseMusterheftExportTabStateArgs): UseMusterheftExportTabStateReturn {
  const floors = useMemo(
    () => projectChildren.filter(isProjectFloor),
    [projectChildren]
  )

  const unassignedRooms = useMemo(
    () => projectChildren.filter(isProjectRoom),
    [projectChildren]
  )

  const unassignedProducts = useMemo(
    () => projectChildren.filter(isProjectProductPosition),
    [projectChildren]
  )

  const resolvedTabID = useMemo((): string | null => {
    const isKnownTab =
      (activeTabID === UNASSIGNED_PRODUCTS_TAB_ID &&
        unassignedProducts.length > 0) ||
      (activeTabID === UNASSIGNED_ROOMS_TAB_ID && unassignedRooms.length > 0) ||
      floors.some(floor => floor.id === activeTabID)

    if (isKnownTab) {
      return activeTabID
    }
    if (floors.length > 0) {
      return floors[0].id
    }
    if (unassignedProducts.length > 0) {
      return UNASSIGNED_PRODUCTS_TAB_ID
    }
    if (unassignedRooms.length > 0) {
      return UNASSIGNED_ROOMS_TAB_ID
    }
    return null
  }, [activeTabID, floors, unassignedProducts, unassignedRooms])

  const visibleChildren = useMemo((): MusterheftExportChild[] => {
    if (resolvedTabID === UNASSIGNED_ROOMS_TAB_ID) {
      return unassignedRooms
    }
    if (resolvedTabID === UNASSIGNED_PRODUCTS_TAB_ID) {
      return unassignedProducts
    }
    const activeFloor = floors.find(floor => floor.id === resolvedTabID)
    return activeFloor?.children ?? []
  }, [resolvedTabID, floors, unassignedRooms, unassignedProducts])

  return {
    floors,
    unassignedRoomsCount: unassignedRooms.length,
    unassignedProductsCount: unassignedProducts.length,
    resolvedTabID,
    visibleChildren
  }
}
